import React, { useEffect, useState } from 'react';
import { useTestId } from '../../hooks/useTestId/useTestId';
import { ITabsProps, TabsSize, UNDERLINE_HIGHLIGHT_SIZE } from './Tabs';
import styled, { css } from 'styled-components';
import { IButtonProps, ButtonElement } from '../Button/Button';
import { IEndIconBaseProps, IStartIconBaseProps } from '../../common/interfaces';
import { DEFAULT_ICON_TYPE } from '../Icon/Icon';
import { TabsContext } from './TabsContext';
import { PX_TO_REM, SIZES } from '../../fundamentals/sizes/sizes';
import { BUTTON_VARIANTS } from '../Button/buttonVariants';
import { BaseDiv } from '../../common/BaseElements';

export interface ITabProps
  extends Omit<React.LiHTMLAttributes<HTMLLIElement>, 'onClick' | 'onMouseEnter' | 'onMouseLeave'>,
    Pick<IButtonProps, 'disabled'>,
    IStartIconBaseProps,
    IEndIconBaseProps,
    Pick<IButtonProps, 'onClick' | 'onMouseEnter' | 'onMouseLeave' | 'badgeLabel'> {}

export type TabPosition = { position: number };

export type ITabsContainerProps = Pick<ITabProps, 'disabled'> & Pick<ITabsProps, 'size' | 'variant' | 'underline'> & Pick<IButtonProps, 'selected'>;

const TabContainer = styled(BaseDiv)<Pick<ITabsContainerProps, 'underline'>>`
  ${({ underline }) =>
    underline === 'bordered' &&
    css`
      position: relative;
      bottom: -${UNDERLINE_HIGHLIGHT_SIZE};
      padding-bottom: -${UNDERLINE_HIGHLIGHT_SIZE};
    `}
`;

const underlinedTabPads: Record<TabsSize, string> = {
  [SIZES.X_SMALL]: `${PX_TO_REM[2]} ${PX_TO_REM[4]}`,
  [SIZES.SMALL]: `${PX_TO_REM[6]} ${PX_TO_REM[4]}`,
  [SIZES.MEDIUM]: `${PX_TO_REM[8]} ${PX_TO_REM[4]}`
};

type FilteredTabVariant = typeof BUTTON_VARIANTS.HERO | typeof BUTTON_VARIANTS.PRIMARY | typeof BUTTON_VARIANTS.SECONDARY;

export const TabButtonBase = styled(ButtonElement)<Pick<ITabsProps, 'underline' | 'size'> & Omit<IButtonProps, 'variant'> & { variant: FilteredTabVariant }>`
  ${({ underline, size, theme, variant, disabled, selected }) =>
    underline &&
    css`
      padding: ${underlinedTabPads[size as TabsSize]};
      border-bottom: solid ${UNDERLINE_HIGHLIGHT_SIZE} ${selected ? theme.buttonColours[variant!].bold.background : 'transparent'};
      &:hover {
        border-bottom-color: ${disabled ? 'transparent' : theme.buttonColours[variant!].bold.background};
      }
      &:active {
        border-bottom-color: ${disabled ? 'transparent' : theme.buttonColours[variant!].bold.background};
      }
    `}
`;

export const Tab = React.forwardRef<HTMLDivElement, ITabProps>(
  (
    {
      children,
      disabled,
      startIconType = DEFAULT_ICON_TYPE,
      startIcon,
      badgeLabel,
      endIcon,
      endIconType = DEFAULT_ICON_TYPE,
      onClick,
      onMouseEnter,
      onMouseLeave,
      ...rest
    },
    ref
  ) => {
    const { generateTestId } = useTestId(rest, 'Tab');
    const { variant, size, underline, active, setActive } = React.useContext(TabsContext);
    const position = ({ ...rest } as TabPosition).position;
    const selected = active === position;
    const cssProps = { selected, variant: variant!, underline, size, disabled };

    const buttonVariantEquivalent = variant === 'accent' ? 'hero' : variant!;
    const buttonVariant = selected ? buttonVariantEquivalent : 'secondary';

    const [computedVariant, setComputedVariant] = useState<FilteredTabVariant>(buttonVariant);

    useEffect(() => {
      setComputedVariant(buttonVariant);
    }, [selected]);

    const handleOnClick: React.MouseEventHandler<HTMLButtonElement> = (e) => {
      setActive(position);
      onClick?.(e);
      e.currentTarget.blur();
    };

    const handleOnMouseEnter: React.MouseEventHandler<HTMLButtonElement> = (e) => {
      setComputedVariant(buttonVariantEquivalent);
      onMouseEnter?.(e);
    };

    const handleOnMouseLeave: React.MouseEventHandler<HTMLButtonElement> = (e) => {
      setComputedVariant(buttonVariant);
      onMouseLeave?.(e);
    };

    return (
      <TabContainer ref={ref} {...generateTestId()} {...cssProps}>
        <TabButtonBase
          underline={underline}
          mode={underline ? 'minimal' : 'bold'}
          bare={!!underline}
          variant={computedVariant}
          selected={selected}
          size={size}
          disabled={disabled}
          startIcon={startIcon}
          startIconType={startIconType}
          endIcon={endIcon}
          endIconType={endIconType}
          onClick={handleOnClick}
          onMouseEnter={handleOnMouseEnter}
          onMouseLeave={handleOnMouseLeave}
          badgeLabel={badgeLabel}
          badgeVariant='secondary' // Update with black opaque
          cssState={variant === 'secondary' && selected ? 'active' : undefined}
          {...generateTestId(`button`)}
        >
          {children}
        </TabButtonBase>
      </TabContainer>
    );
  }
);
